import { Component, OnInit, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { JobService, OrderService } from '../../../services';
import { RfidProductionService } from '../../../services/rfid-production.service';
import { 
  QUEUED, COLLATING, COLLATED, LAMINATING, LAMINATED, 
  CUT, VALIDATING, VALIDATED, PACKING, 
  PRINTED
} from '../../../../ngrx/status-types';
import { SET_JOB_DETAILS_VIEW } from '../../../../ngrx/actions';
import { BsModalService } from 'ngx-bootstrap';
import { TranslateService } from 'app/i18n';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import { getScrapReasonsForStatus, SCRAP_CATEGORIES, ScrapCategory, ScrapReason } from '../../models/scrap-reasons.model';

@Component({
  selector: 'rfid-reprint-modal',
  templateUrl: 'rfid-reprint-modal.component.html',
  styleUrls: ['rfid-reprint-modal.component.scss']
})
export class RfidReprintModal implements OnInit, OnChanges, OnDestroy {
  @Input('reprintableJobs') reprintableJobs;
  @Input('job') job: any;
  @Input('currentStatus') currentStatus: string;

  reprintForm: FormGroup;
  jobSheetQty: number;

  // Scrap reason related properties
  applicableScrapCategories: ScrapCategory[] = [];
  selectedScrapCategory: ScrapCategory;
  selectedScrapReason: ScrapReason;

  constructor(
    private orderService: OrderService,
    private jobService: JobService,
    private store: Store<any>,
    private translate: TranslateService,
    private modalService: BsModalService,
    private fb: FormBuilder,
    private rfidProductionService: RfidProductionService
  ) {}

  ngOnInit(): void {
    this.createReprintForm();
    if (this.job) {
      this.jobSheetQty = this.job.sheets;
      if(this.job.rfid_missingSheetQty){
        console.log('Missing sheets detected')
        this.jobSheetQty = this.job.rfid_missingSheetQty;
      }

      this.reprintForm.patchValue({
        allowPartialReprint: false,
        sideFRONTEnabled: true,
        sideBACKEnabled: true,
        sideFRONTQuantity: this.jobSheetQty,
        sideBACKQuantity: this.jobSheetQty

        // sideBACKQuantity: this.job.sheets,
        // sideFRONTQuantity: this.job.sheets,
      });

      // Reset it back
      this.reprintForm.get('allowPartialReprint').valueChanges.subscribe(allowPartial => {
        if (!allowPartial) {
          this.reprintForm.patchValue({
            sideFRONTQuantity: this.jobSheetQty,
            sideBACKQuantity: this.jobSheetQty

            // sideFRONTQuantity: this.job.sheets,
            // sideBACKQuantity: this.job.sheets
          });
        }
      });
    }

    // Initialize scrap categories based on the current job status
    this.updateScrapCategories(this.currentStatus);
    this.reprintForm.get('scrapCategoryId').valueChanges.subscribe(categoryName => {
      if (categoryName) {
        this.selectedScrapCategory = this.applicableScrapCategories.find(
          category => category.name === categoryName
        );
        // Reset the scrap reason when category changes
        this.reprintForm.get('scrapReasonCode').setValue('');
      } else {
        this.selectedScrapCategory = null;
      }
    });
    
    this.reprintForm.get('scrapReasonCode').valueChanges.subscribe(reasonCode => {
      if (reasonCode && this.selectedScrapCategory) {
        this.selectedScrapReason = this.selectedScrapCategory.reasons.find(
          reason => reason.code === reasonCode
        );
      } else {
        this.selectedScrapReason = null;
      }
    });
  }

  ngOnDestroy(): void {
    console.log('ngOnDestroy');
    // Cleanup any subscriptions or resources
    this.selectedScrapCategory = null;
  }

  ngOnChanges(changes: SimpleChanges) {
    // When job changes, update scrap categories
    if (changes.job && changes.job.currentValue) {
      this.job = changes.job.currentValue;
      this.currentStatus = this.job.status || this.currentStatus;
      this.updateScrapCategories(this.currentStatus);
      
      // If the form is already created, update it
      if (this.reprintForm) {
        // Update form values based on new job
        this.reprintForm.get('sideFRONTQuantity').setValue(this.job.sheets);
        this.reprintForm.get('sideBACKQuantity').setValue(this.job.sheets);
        // Update validators
        const sheetsValidators = [
          Validators.required,
          Validators.min(1),
          Validators.max(this.job.sheets)
        ];
        this.reprintForm.get('sideFRONTQuantity').setValidators(sheetsValidators);
        this.reprintForm.get('sideBACKQuantity').setValidators(sheetsValidators);
        // Update inlay scrap validators
        this.reprintForm.get('inlayScrapQuantity').setValidators([
          Validators.required,
          Validators.min(0),
          Validators.max(this.job.sheets)
        ]);
      }
    }
  }

  createReprintForm(): void {
    this.reprintForm = this.fb.group({
      allowPartialReprint: [false],
      redownloadArtwork: [false],
      sideFRONTEnabled: [true],
      sideBACKEnabled: [true],
      sideFRONTQuantity: [{
        value: this.job.sheets,
        disabled: true
      }, [
        Validators.required,
        Validators.min(1),
        Validators.max(this.job.sheets)
      ]],
      sideBACKQuantity: [{
        value: this.job.sheets,
        disabled: true
      }, [
        Validators.required,
        Validators.min(1),
        Validators.max(this.job.sheets)
      ]],
      // Add new form controls for inlay scrap tracking
      inlaysSaved: [true],
      inlayScrapQuantity: [{
        value: 0,
        disabled: true
      }, [
        Validators.required,
        Validators.min(0),
        Validators.max(this.job.sheets)
      ]],
      // New form controls for scrap reason
      scrapCategoryId: [null],
      scrapReasonCode: ['', Validators.required]
    });

    this.reprintForm.get('allowPartialReprint').valueChanges.subscribe(allowPartial => {
      if (allowPartial) {
        this.reprintForm.get('sideFRONTQuantity').enable();
        this.reprintForm.get('sideBACKQuantity').enable();
      } else {
        this.reprintForm.get('sideFRONTQuantity').disable();
        this.reprintForm.get('sideBACKQuantity').disable();
      }
    });

    // Add listener for inlaysSaved toggle
    this.reprintForm.get('inlaysSaved').valueChanges.subscribe(saved => {
      if (saved) {
        this.reprintForm.get('inlayScrapQuantity').disable();
        this.reprintForm.get('inlayScrapQuantity').setValue(0);
      } else {
        this.reprintForm.get('inlayScrapQuantity').enable();
        // Set a default value based on the minimum of front/back sheets
        const frontSheetsScrap = this.getSideQuantity('FRONT');
        const backSheetsScrap = this.getSideQuantity('BACK');
        const defaultScrap = Math.max(frontSheetsScrap, backSheetsScrap);
        this.reprintForm.get('inlayScrapQuantity').setValue(defaultScrap);
      }
    });
  }

  getSideQuantity(side: string): number {
    if (side === 'FRONT') {
      return this.reprintForm.get('sideFRONTEnabled').value ? 
             this.reprintForm.get('sideFRONTQuantity').value : 0;
    } else if (side === 'BACK') {
      return this.reprintForm.get('sideBACKEnabled').value ? 
             this.reprintForm.get('sideBACKQuantity').value : 0;
    }
    return 0;
  }

  getTotalCards(side: 'FRONT' | 'BACK'): number {
    const sheetQty = this.getSideQuantity(side);
    return sheetQty * (this.job.itemsPerSheet || 0);
  }

  private buildReprintReason(
    sideFRONTEnabled: boolean,
    sideBACKEnabled: boolean,
    allowPartialReprint: boolean,
    updatedJob: any
  ): string {
    
    const sideDetails = [];
    let totalSheets = 0;

    
    const sides: Array<{ side: 'FRONT' | 'BACK', enabled: boolean }> = [
      { side: 'FRONT', enabled: sideFRONTEnabled },
      { side: 'BACK', enabled: sideBACKEnabled }
    ];

    sides.forEach(({ side, enabled }) => {
      // Calculate quantities (zero if disabled)
      const sheets = enabled ? this.getSideQuantity(side) : 0;
      const cards = enabled ? this.getTotalCards(side) : 0;
      
      // Add to running total and details
      totalSheets += sheets;
      sideDetails.push(`Side ${side}:${sheets} sheets/${cards} cards`);

      
      if (enabled) {
        updatedJob.rfid_artworks[side.toLowerCase()].artwork_status = 'pending';
        updatedJob.rfid_artworks[side.toLowerCase()].sheet_print_qty = sheets;
      } else {
        // updatedJob.rfid_artworks[side.toLowerCase()].artwork_status = 'completed';
        updatedJob.rfid_artworks[side.toLowerCase()].sheet_print_qty = 0;
      }
    });


    const tokens = [
      totalSheets        ,                                        // [1] Total sheets to reprint
      'HP'               ,                                        // [2] Machine type
      allowPartialReprint ? 'Partial Reprint' : 'Full Reprint',   // [3] Print type
      sideDetails.join   (',')                                    // [4] Detailed breakdown per side
    ];

    return `|${tokens.join('|')}|`;
  }

  reprintJob(): void {
    if (!this.reprintableJobs.length) return;

    this.store.dispatch({
      type: SET_JOB_DETAILS_VIEW,
      payload: { loading: true }
    });

    const { allowPartialReprint, sideFRONTEnabled, sideBACKEnabled, redownloadArtwork } = this.reprintForm.value;

    if(redownloadArtwork){
      this.jobService.downloadArtworks(this.job.id).subscribe(
        (res) => {
          console.log("🚀 ~ RfidReprintModal ~ reprintJob ~ res:", res)
        },
        (error) => {
          console.error('Error redownloading artwork:', error);
        }
      );
    }

    const updatedJob = JSON.parse(JSON.stringify(this.job));

    const reprintReason = this.buildReprintReason(
      sideFRONTEnabled,
      sideBACKEnabled,
      allowPartialReprint,
      updatedJob
    );
    
    // Record material scrap based on current status before changing to QUEUED
    this.recordMaterialScrap().subscribe(
      () => {
        console.log('Successfully recorded material scrap for reprint');
        this.completeReprintProcess(updatedJob, reprintReason);
      },
      error => {
        console.error('Error recording material scrap:', error);
        // Continue with reprint even if scrap recording fails
        this.completeReprintProcess(updatedJob, reprintReason);
      },
      () => {
        // This completion handler ensures we get notified when the observable completes
        console.log('Material scrap recording process completed');
      }
    );
  }

  // Create a separate method to handle the actual status change process
  private completeReprintProcess(updatedJob: any, reprintReason: string): void {
    this.orderService.setJobStatus(
      updatedJob,
      QUEUED,
      this.translate.instant(reprintReason),
      true,
      ""
    );
    this.modalService.hide(1);
  }

  /**
   * Records material scrap based on the current status of the job
   * Each status scraps different materials depending on how far along the process the job was
   */
  private recordMaterialScrap(): Observable<any> {
    // Create a job data object for the production service
    const jobData = {
      id: this.job.id,
      orderId: this.job.orderId || '',
      rfid_code: this.job.rfid_code || '',
      rfid_chipType: this.job.rfid_chipType || '',
      rfid_tech: this.job.rfid_tech || '',
      rfid_finishType: this.job.rfid_finishType || '',
      qty: this.job.qty || 0,
      reprintCount: this.job.reprintCount || 0,
      rfid_inlayPartNumber: this.job.rfid_inlayPartNumber || this.job.inlay_part_number || '',
      product: this.job.product.sku || this.job.product || '',
      sheets: this.job.sheets || 0
    };

    // Calculate scrap quantities based on the side settings
    const frontSheetsScrap = this.getSideQuantity('FRONT');
    const backSheetsScrap = this.getSideQuantity('BACK');
    
    // Get the square footage per sheet for laminate calculations
    const SHEET_WIDTH_INCHES = 18;
    const SHEET_HEIGHT_INCHES = 12;
    const squareFeetPerSheet = (SHEET_WIDTH_INCHES * SHEET_HEIGHT_INCHES) / 144;

    // Calculate laminate square footage
    const frontLaminateFeetScrap = frontSheetsScrap * squareFeetPerSheet;
    const backLaminateFeetScrap = backSheetsScrap * squareFeetPerSheet;

    // Determine inlay scrap - use maximum of front and back sheets if they differ
    let inlaySheetScrap = 0;
    if (frontSheetsScrap !== backSheetsScrap) {
      inlaySheetScrap = Math.max(frontSheetsScrap, backSheetsScrap);
    } else {
      inlaySheetScrap = frontSheetsScrap;
    }

    // Status order: PRINTED → COLLATING → COLLATED → LAMINATING → LAMINATED → CUT → VALIDATING → VALIDATED
    
    // Create status-appropriate scrap record if reason is selected
    let scrapRecord = null;
    if (this.selectedScrapReason) {
      // Base scrap record info
      scrapRecord = {
        scrap_category: this.selectedScrapCategory.name,
        scrap_code: this.selectedScrapReason.code,
        scrap_reason: this.selectedScrapReason.description,
        job_status: this.currentStatus,
        front_sheets_scrap: frontSheetsScrap,
        back_sheets_scrap: backSheetsScrap
      };
      
      // Add inlay scrap data only for statuses after PRINTED
      const inlayTrackingStatuses = [COLLATING, COLLATED, LAMINATING, LAMINATED, CUT, VALIDATING, VALIDATED, PACKING];
      if (inlayTrackingStatuses.indexOf(this.currentStatus) >= 0) {
        // For COLLATING/COLLATED, use the user input value
        if ([COLLATING, COLLATED].indexOf(this.currentStatus) >= 0) {
          if (!this.reprintForm.get('inlaysSaved').value) {
            scrapRecord['inlay_sheets_scrap'] = this.reprintForm.get('inlayScrapQuantity').value || 0;
          }
        } else {
          // For post-collated statuses, always include inlay scrap
          scrapRecord['inlay_sheets_scrap'] = inlaySheetScrap;
        }
      }
      
      // Add laminate scrap data only for statuses after COLLATED
      const laminateTrackingStatuses = [LAMINATING, LAMINATED, CUT, VALIDATING, VALIDATED, PACKING];
      if (laminateTrackingStatuses.indexOf(this.currentStatus) >= 0) {
        scrapRecord['front_laminate_scrap'] = frontLaminateFeetScrap;
        scrapRecord['back_laminate_scrap'] = backLaminateFeetScrap;
      }
    }

    // Array to hold our API calls
    const apiCalls: Observable<any>[] = [];

    // Always record paper scrap
    apiCalls.push(
      this.rfidProductionService.recordPaperSheetConsumption(
        this.job.id,
        jobData,
        {
          front_sheets_used: 0,
          back_sheets_used: 0,
          front_sheets_scrap: frontSheetsScrap,
          back_sheets_scrap: backSheetsScrap,
          scrap_record: scrapRecord
        }
      ).catch(error => {
        console.error('Error recording paper scrap:', error);
        return Observable.of(null);
      })
    );

    // For COLLATING and onwards: Use the user input to determine inlay scrap
    const inlayTrackingStatuses = [COLLATING, COLLATED, LAMINATING, LAMINATED, CUT, VALIDATING, VALIDATED, PACKING];
    if (inlayTrackingStatuses.indexOf(this.currentStatus) >= 0) {
      // Only record inlay scrap if inlays were not saved
      if (!this.reprintForm.get('inlaysSaved').value) {
        const inlayScrapQty = this.reprintForm.get('inlayScrapQuantity').value || 0;
        
        apiCalls.push(
          this.rfidProductionService.recordInlaySheetConsumption(
            this.job.id,
            jobData,
            {
              inlay_sheets_used: 0,
              inlay_sheets_scrap: inlayScrapQty,
              inlay_part_number: this.job.inlay_part_number || '',
              // Don't include scrap_record here as it's already in the paper consumption call
            }
          ).catch(error => {
            console.error('Error recording inlay scrap:', error);
            return Observable.of(null);
          })
        );
      }
    } 
    // // For statuses after LAMINATED: Always record inlay scrap
    // else {
    //   const postLaminateStatuses = [LAMINATED, CUT, VALIDATING, VALIDATED, PACKING];
    //   if (postLaminateStatuses.indexOf(this.currentStatus) >= 0) {
    //     // For post-laminate, always scrap inlays
    //     apiCalls.push(
    //       this.rfidProductionService.recordInlaySheetConsumption(
    //         this.job.id,
    //         jobData,
    //         {
    //           inlay_sheets_used: 0,
    //           inlay_sheets_scrap: inlaySheetScrap,
    //           inlay_part_number: this.job.inlay_part_number || '',
    //           // Don't include scrap_record here as it's already in the paper consumption call
    //         }
    //       ).catch(error => {
    //         console.error('Error recording inlay scrap:', error);
    //         return Observable.of(null);
    //       })
    //     );
    //   }
    // }
    
    const statusesRequiringLaminateScrap = [LAMINATING, LAMINATED, CUT, VALIDATING, VALIDATED];
    if (statusesRequiringLaminateScrap.indexOf(this.currentStatus) >= 0) {
      // Get laminate type
      const laminateType = this.job.rfid_finishType || '';
      
      apiCalls.push(
        this.rfidProductionService.recordLaminateConsumption(
          this.job.id,
          jobData,
          {
            front_laminate_feet_used: 0,
            back_laminate_feet_used: 0,
            front_laminate_scrap: frontLaminateFeetScrap,
            back_laminate_scrap: backLaminateFeetScrap,
            laminate_type: laminateType,
            // Don't include scrap_record here as it's already in the paper consumption call
          }
        ).catch(error => {
          console.error('Error recording laminate scrap:', error);
          return Observable.of(null);
        })
      );
    }

    // When there are no API calls, we need to return a completed observable
    if (apiCalls.length === 0) {
      return Observable.of(null);
    }
    
    // If there are multiple API calls, run them in parallel and wait for all to complete
    return apiCalls.length > 1 ? Observable.forkJoin(apiCalls) : apiCalls[0];
  }

  isFormValid(): boolean {
    const form = this.reprintForm;
    if (!form) return false;
    
    const sideFRONTEnabled = form.get('sideFRONTEnabled').value;
    const sideBACKEnabled = form.get('sideBACKEnabled').value;
    
    // One has to be enabled atleast to reprint...
    if (!sideFRONTEnabled && !sideBACKEnabled) {
      return false;
    }
    
    if (!form.get('allowPartialReprint').value) {
      return true;
    }
    
    if (sideFRONTEnabled && !form.get('sideFRONTQuantity').valid) {
      return false;
    }
    
    if (sideBACKEnabled && !form.get('sideBACKQuantity').valid) {
      return false;
    }
    
    return true;
  }

  /**
   * Determines if we should show the inlay scrap tracking section
   * Only show for COLLATING and onwards
   */
  showInlayScrapSection(): boolean {
    const inlayTrackingStatuses = [COLLATING, COLLATED, LAMINATING, LAMINATED, CUT, VALIDATING, VALIDATED, PACKING];
    return inlayTrackingStatuses.indexOf(this.currentStatus) >= 0;
  }

  /**
   * Update the available scrap categories based on job status
   */
  updateScrapCategories(status: string): void {
    console.log("🚀 ~ status:", status)
    this.applicableScrapCategories = getScrapReasonsForStatus(status);
    console.log("🚀 ~ this.applicableScrapCategories:", this.applicableScrapCategories)
    
    // If there's only one category, select it automatically
    if (this.applicableScrapCategories.length === 1) {
      this.reprintForm.get('scrapCategoryId').setValue(this.applicableScrapCategories[0].name);
      this.selectedScrapCategory = this.applicableScrapCategories[0];
    } else if (this.applicableScrapCategories.length > 0) {
      // Clear the selection when there are multiple options
      this.reprintForm.get('scrapCategoryId').setValue('');
    }
  }

  // Make sure we update scrap categories when job changes
  updateJob(job: any): void {
    this.job = job;
    this.currentStatus = job.status;
    this.updateScrapCategories(this.currentStatus);
    // Any other job initialization...
  }
}


// WEBPACK FOOTER //
// /Users/itu/Desktop/impreso_updated_sheets/impresocloud/www/client/src/app/shared/modals/rfid-reprint-modal/rfid-reprint-modal.component.ts